import { GeneralPermissions } from "@redotech/account-common/permissions";
import { OmsFlagKey } from "@redotech/feature-flags-keys-oms/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { AlertContent } from "@redotech/redo-merchant-app-common/alert";
import { AskRedoSlidePanelProvider } from "@redotech/redo-merchant-app-common/ask-redo/ask-redo-slide-panel-context";
import { AuthRequired } from "@redotech/redo-merchant-app-common/auth";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { EscalationProvider } from "@redotech/redo-merchant-app-common/escalation";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import {
  TeamAndUserRequired,
  TeamContext,
} from "@redotech/redo-merchant-app-common/team";
import { UserCacheContext } from "@redotech/redo-merchant-app-common/user-cache";
import { VoiceContextV2Provider } from "@redotech/redo-merchant-support-app/v2/voice/voice-context-v2-provider";
import { VoiceWidgetV2 } from "@redotech/redo-merchant-support-app/v2/voice/voice-widget-v2";
import { VoiceWidget } from "@redotech/redo-merchant-support-app/voice/voice-widget";
import { VoiceProvider } from "@redotech/redo-support-settings-common/voice-settings";
import { ActionScopeProvider } from "@redotech/redo-web/actions/action-scope-context";
import { ErrorBoundary } from "@redotech/redo-web/error-page";
import { Page as CommonPage } from "@redotech/redo-web/page";
import { TreeProvider } from "@redotech/redo-web/tree/tree-context";
import { memo, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { useEffect_conversionNotifications } from "../conversion/common/hooks/use-conversion-notifications";
import { FirstInstallRedirectGuard } from "../onboarding/first-install-redirect-guard";
import { ReturnsOnboardingResumeStateProvider } from "../onboarding/returns-onboarding-resume-state";
import { DataServices } from "../services/data-services";
import { registerDisputesProduct } from "../setup-guide/products/disputes/register";
import { registerShopperAgentProduct } from "../setup-guide/products/shopper-agent/register";
import { SetupGuideProvider } from "../setup-guide/provider/setup-guide-provider";
import { SetupGuideHost } from "../setup-guide/setup-guide-host";
import { AskRedoLayout } from "./ask-redo/layout/ask-redo-layout";
import { NotificationBanner } from "./banner/banner";
import { SHIPPING_SHIPMENT_DETAILS_ROUTE } from "./navigation/routing/shipping-route-matching";
import { MerchantAppSideNav } from "./navigation/side-navigation/merchant-side-navigation";
import { FeedbackCalloutHost } from "./navigation/top-navigation/feedback-callout-host";
import { GlobalSearch } from "./navigation/top-navigation/global-search";
import { MerchantAppTopNav } from "./navigation/top-navigation/merchant-top-navigation";
import * as styles from "./page.module.css";
import { MerchantPromptHost } from "./prompts/merchant-prompt-host";
import { isWmsRoute } from "./wms-route";

// Register the disputes product config once at module load. Idempotent.
registerDisputesProduct();
registerShopperAgentProduct();

function urlIsInSettings(url: string): boolean {
  const matchesStoreUrl = matchPath("/stores/:storeId/settings/*", url);
  const matchesSettingsRoute = matchPath("/settings/*", url);
  return !!(matchesStoreUrl || matchesSettingsRoute);
}

// Do not show incident banners for warehouse routes. Warehouse workers do not care about incidents.
function isBannerRoute(url: string): boolean {
  const hiddenPaths = [
    "/stores/:storeId/order-management/outbound-labels/pick-sessions/*",
    "/stores/:storeId/order-management/outbound-labels/pick-sessions",
    "/stores/:storeId/order-management/outbound-labels/create-pick-session/*",
    "/stores/:storeId/order-management/outbound-labels/create-pick-session",
    "/stores/:storeId/order-management/wms/work-orders/detail/:workOrderId",
    "/stores/:storeId/order-management/warehouse",
    "/stores/:storeId/order-management/warehouse/*",
    "/stores/:storeId/shipping/warehouse",
    "/stores/:storeId/shipping/warehouse/*",
  ];
  return !hiddenPaths.some((path) => matchPath(path, url));
}

function isShippingShipmentDetailsRoute(url: string): boolean {
  return !!matchPath(SHIPPING_SHIPMENT_DETAILS_ROUTE, url);
}

function isShippingShipmentDetailsProductivityRoute(url: string): boolean {
  return isShippingShipmentDetailsRoute(url);
}

function isScanToVerifyDetailProductivityRoute(url: string): boolean {
  return !!(
    matchPath(
      "/stores/:storeId/shipping/outbound-labels/scan/:fulfillmentGroupId",
      url,
    ) ||
    matchPath(
      "/stores/:storeId/order-management/outbound-labels/scan/:fulfillmentGroupId",
      url,
    )
  );
}

export const Page = memo(function Page({
  hideHeader = () => false,
  hideNavbar = () => false,
  hidePadding = (url: string) => url.includes("ai-builder"),
  hideSearchBarAndBanner = () => false,
}: {
  hideHeader?: (url: string, search: { search: string }) => boolean;
  hideNavbar?: (url: string, search: { search: string }) => boolean;
  hidePadding?: (url: string, search: { search: string }) => boolean;
  hideSearchBarAndBanner?: (url: string) => boolean;
}) {
  return (
    <AuthRequired>
      <TeamAndUserRequired>
        <ReturnsOnboardingResumeStateProvider>
          <DataServices>
            <UserCacheContext.Provider value={new Map()}>
              <SetupGuideProvider>
                <FirstInstallRedirectGuard>
                  <PageWithContext
                    hideHeader={hideHeader}
                    hideNavbar={hideNavbar}
                    hidePadding={hidePadding}
                    hideSearchBarAndBanner={hideSearchBarAndBanner}
                  />
                  <SetupGuideHost />
                  <MerchantPromptHost />
                </FirstInstallRedirectGuard>
              </SetupGuideProvider>
            </UserCacheContext.Provider>
          </DataServices>
        </ReturnsOnboardingResumeStateProvider>
      </TeamAndUserRequired>
    </AuthRequired>
  );
});

const PageWithContext = memo(function PageWithContext({
  hideHeader = () => false,
  hideNavbar = () => false,
  hidePadding = (url: string) => url.includes("ai-builder"),
  hideSearchBarAndBanner = () => false,
}: {
  hideHeader?: (url: string, search: { search: string }) => boolean;
  hideNavbar?: (url: string, search: { search: string }) => boolean;
  hidePadding?: (url: string, search: { search: string }) => boolean;
  hideSearchBarAndBanner?: (url: string) => boolean;
}) {
  const team = useRequiredContext(TeamContext);
  const voiceProvider =
    team.settings.support?.voice?.provider ?? VoiceProvider.TELNYX;

  const [lastNonSettingsUrl, setLastNonSettingsUrl] = useState<
    string | undefined
  >(undefined);

  const location = useLocation();

  const stableHideNavbar = useHandler(hideNavbar);
  const stableHidePadding = useHandler(hidePadding);
  const stableHideSearchBarAndBanner = useHandler(hideSearchBarAndBanner);

  const shouldHidePadding = useMemo(() => {
    return stableHidePadding(location.pathname, { search: location.search });
  }, [location, stableHidePadding]);

  const newShipmentDetailsRoute =
    isShippingShipmentDetailsProductivityRoute(location.pathname) &&
    team.featureFlags?.[OmsFlagKey.NEW_SHIPMENT_DETAILS] === true &&
    team.settings.outboundLabels?.newOmsEnabled === true;
  const scanToVerifyDetailRoute = isScanToVerifyDetailProductivityRoute(
    location.pathname,
  );

  const hideNavigation = useMemo(() => {
    return (
      newShipmentDetailsRoute ||
      scanToVerifyDetailRoute ||
      stableHideNavbar(location.pathname, { search: location.search })
    );
  }, [
    location,
    newShipmentDetailsRoute,
    scanToVerifyDetailRoute,
    stableHideNavbar,
  ]);

  const shouldHideSearchBarAndBanner = useMemo(() => {
    return stableHideSearchBarAndBanner(location.pathname);
  }, [location, stableHideSearchBarAndBanner]);

  useEffect(() => {
    if (!urlIsInSettings(location.pathname)) {
      setLastNonSettingsUrl(location.pathname);
    }
  }, [location]);

  const routes = useRequiredContext(MerchantRoutesContext);
  const canManageUsers = useHasPermission(GeneralPermissions.MANAGE_TEAM_USERS);

  const settingsUrl = canManageUsers
    ? routes.getSettingsSubRoute("users")
    : routes.getSettingsSubRoute("personal/profile");

  const nonSettingsUrl = lastNonSettingsUrl || `/stores/${team._id}/summary`;
  const settingsRoot = `/stores/${team._id}/settings`;

  useEffect_conversionNotifications();

  const wmsRoute = isWmsRoute(location.pathname);
  const fullPageProductivityRoute =
    newShipmentDetailsRoute || scanToVerifyDetailRoute;

  const hideGlobalSearchTopBar =
    wmsRoute || fullPageProductivityRoute || shouldHideSearchBarAndBanner;
  const showBanner =
    isBannerRoute(location.pathname) &&
    !shouldHideSearchBarAndBanner &&
    !wmsRoute &&
    !fullPageProductivityRoute;

  // Shared between both voice widget variants below.
  const pageBody = (
    <>
      <CommonPage
        banner={showBanner ? <NotificationBanner /> : undefined}
        ErrorBoundary={ErrorBoundary}
        hideHeader={(url, search) =>
          fullPageProductivityRoute || hideHeader(url, search)
        }
        hideOuterTopPadding={!hideGlobalSearchTopBar}
        hidePadding={shouldHidePadding}
        nav={
          <MerchantAppSideNav
            enterSettingsUrl={settingsUrl}
            exitSettingsUrl={nonSettingsUrl}
            hideNav={hideNavigation}
            hideTeamSwitcher
            settingsRoot={settingsRoot}
          />
        }
        newChrome
        skipFirstBreadcrumbIfMatches={["Order management"]}
      />
      <AlertContent />
      <FeedbackCalloutHost />
    </>
  );

  return (
    <TreeProvider>
      <ActionScopeProvider>
        {/* AskRedoSlidePanelProvider wraps GlobalSearch because the
            top-nav command palette's "Ask AI" handoff calls
            `useAskRedoSlidePanel()`. */}
        <AskRedoSlidePanelProvider>
          <GlobalSearch>
            <div className={styles.pageShell}>
              {!hideGlobalSearchTopBar && <MerchantAppTopNav />}
              {/* EscalationProvider wraps AskRedoLayout so the watcher
                  and slide-out share escalation state. */}
              <EscalationProvider>
                <AskRedoLayout>
                  {/* Wraps routed pages too — the voice session isn't just for the floating widget. */}
                  <VoiceContextV2Provider>
                    {pageBody}
                    {voiceProvider === VoiceProvider.AIRCALL ? (
                      <VoiceWidget />
                    ) : (
                      <VoiceWidgetV2 />
                    )}
                  </VoiceContextV2Provider>
                </AskRedoLayout>
              </EscalationProvider>
            </div>
          </GlobalSearch>
        </AskRedoSlidePanelProvider>
      </ActionScopeProvider>
    </TreeProvider>
  );
});
